import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { OnboardingSession } from 'shared'
import { Alert, Badge, Button, Card, CopyField } from '../ui'
import { api } from '../../utils/api'

/** Where applying happens: the page a company is sent to together with the code. */
const APPLY_URL = `${window.location.origin}/onboarding`

/**
 * Why a session does not take applications, in one word. Open is the one state in
 * which the code works; the others differ in what the admin can do about them.
 */
export function SessionStatus({ session }: { session: OnboardingSession }) {
  const { t } = useTranslation()

  if (session.is_open) return <Badge variant="success">{t('onboarding.open')}</Badge>
  if (session.status === 'closed') return <Badge variant="neutral">{t('onboarding.closed')}</Badge>
  // Open in the table but the code does not work: the date passed, or every place is taken.
  if (session.pending_count + session.accepted_count >= session.max_applications) {
    return <Badge variant="warning">{t('onboarding.full')}</Badge>
  }
  return <Badge variant="warning">{t('onboarding.expired')}</Badge>
}

/** The code of a session just created: it can be seen again from the session's page. */
export function CodeReveal({ code }: { code: string }) {
  const { t } = useTranslation()

  return (
    <div className="rounded-md border border-primary-200 bg-primary-50 p-4 dark:border-primary-900 dark:bg-primary-500/10">
      <p className="text-sm font-medium text-primary-900 dark:text-primary-200">
        {t('onboarding.codeReady')}
      </p>
      <p className="mt-0.5 text-xs text-primary-700 dark:text-primary-300">
        {t('onboarding.codeReadyHelp', { url: APPLY_URL })}
      </p>
      <div className="mt-3">
        <CopyField value={code} label={t('onboarding.code')} className="font-mono text-lg tracking-widest" />
      </div>
    </div>
  )
}

/**
 * What to hand to a company: the page to open and the code to type there. Always
 * on the session's page, so nobody has to remember either. `reloadKey` changes
 * when the code is replaced; `canReplace` says whether this person can do it.
 */
export function AccessCard({
  sessionId,
  reloadKey,
  canReplace,
}: {
  sessionId: string
  reloadKey: number
  canReplace: boolean
}) {
  const { t } = useTranslation()
  // `undefined`: still loading. `null`: no readable code. `'error'`: could not ask.
  const [code, setCode] = useState<string | null | undefined | 'error'>(undefined)
  const [attempt, setAttempt] = useState(0)
  // Whether this server can take applications at all: without the anti-bot keys every code is refused.
  const [antibot, setAntibot] = useState(true)

  useEffect(() => {
    let cancelled = false
    setCode(undefined)
    api
      .get<{ data: { code: string | null; antibot_configured?: boolean } }>(
        `/api/onboarding/sessions/${sessionId}/code`,
      )
      .then((response) => {
        if (cancelled) return
        setCode(response.data.code)
        setAntibot(response.data.antibot_configured !== false)
      })
      .catch(() => !cancelled && setCode('error'))
    return () => {
      cancelled = true
    }
  }, [sessionId, reloadKey, attempt])

  return (
    <Card className="space-y-4 p-5">
      <div>
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          {t('onboarding.accessTitle')}
        </h2>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {t('onboarding.accessHelp')}
        </p>
      </div>
      {!antibot && (
        <Alert variant="warning">
          <strong className="font-semibold">{t('onboarding.antibotMissingTitle')}</strong>{' '}
          {t('onboarding.antibotMissing')}
        </Alert>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">
            {t('onboarding.link')}
          </p>
          <CopyField value={APPLY_URL} label={t('onboarding.link')} className="text-xs" />
        </div>
        <div>
          <p className="mb-1.5 text-xs font-medium text-gray-700 dark:text-gray-300">
            {t('onboarding.code')}
          </p>
          {code === undefined ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          ) : code === 'error' ? (
            <Alert variant="warning" className="flex items-center justify-between gap-3">
              <span>{t('onboarding.codeLoadError')}</span>
              <Button variant="secondary" size="sm" onClick={() => setAttempt((value) => value + 1)}>
                {t('onboarding.retry')}
              </Button>
            </Alert>
          ) : code === null ? (
            <Alert variant="info">
              {t(canReplace ? 'onboarding.codeUnavailable' : 'onboarding.askAdminForCode')}
            </Alert>
          ) : (
            <CopyField value={code} label={t('onboarding.code')} className="font-mono tracking-widest" />
          )}
        </div>
      </div>
    </Card>
  )
}

/** The three counters of a session, as tiles. */
export function SessionCounts({ session }: { session: OnboardingSession }) {
  const { t } = useTranslation()

  return (
    <dl className="grid grid-cols-3 gap-4">
      {(
        [
          ['pending', session.pending_count],
          ['accepted', session.accepted_count],
          ['rejected', session.rejected_count],
        ] as const
      ).map(([key, value]) => (
        <Card key={key} className="p-4 text-center">
          <dd className="text-2xl font-semibold text-gray-900 dark:text-gray-50">{value}</dd>
          <dt className="text-xs text-gray-500 dark:text-gray-400">{t(`onboarding.${key}`)}</dt>
        </Card>
      ))}
    </dl>
  )
}
